import {
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import JsonApiResponse, { Links, Meta } from '@/models/JsonApiResponse';
import {
  getNextPageParam,
  getPreviousPageParam,
} from '@/utils/infinityQueries';
import { useInfiniteCache } from './query/v2/useCacheManager';
import { Model as BaseModel } from '@/models/Model';
import { InfiniteCacheManager } from './query/v2/CacheFacade';

export type UseInfiniteServiceOptions<
  Model,
  TError = unknown,
  TQueryKey extends QueryKey = QueryKey,
> = UseInfiniteQueryOptions<
  JsonApiResponse<Model>,
  TError,
  JsonApiResponse<Model>,
  JsonApiResponse<Model>,
  TQueryKey
>;

export type UseInfiniteServiceResult<
  Model extends BaseModel,
  TError = unknown,
> = {
  results: Model[];
  meta?: Meta;
  links?: Links[];
  invalidate: () => Promise<void>;
  cacheManager: InfiniteCacheManager<Model>;
} & Omit<UseInfiniteQueryResult<JsonApiResponse<Model>, TError>, 'data'>;

export default function useInfiniteService<
  Model extends BaseModel,
  TError = unknown,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseInfiniteServiceOptions<Model, TError, TQueryKey>,
): UseInfiniteServiceResult<Model, TError> {
  const { data, ...rest } = useInfiniteQuery({
    getNextPageParam,
    getPreviousPageParam,
    ...options,
  });

  const results = data?.pages?.flatMap(page => page?.results) ?? [];

  const meta = data?.pages?.at(-1)?.meta;

  const links = data?.pages?.flatMap(page => page?.links) ?? [];

  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: options.queryKey });

  const cacheManager = useInfiniteCache<Model>(options.queryKey as QueryKey);
  return { ...rest, results, meta, links, invalidate, cacheManager };
}
